/**
 * Task Editor Form
 *
 * Form content for editing/creating tasks.
 * Extracted from TaskEditDialog.tsx for use in the in-drawer editor panel.
 */

import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TaskStatus, TaskPriority, TaskScope } from '@/types/notepad';
import { useTask, useUpdateTask, useCreateTask } from '@/hooks/useMyTasks';
import { useOnboarding } from '@/hooks/useTeamOnboarding';
import { useActiveTeamId } from '@/contexts/TeamContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { NotepadEditorMode } from './useNotepadEditorState';

interface TaskEditorFormProps {
  mode: NotepadEditorMode;
  taskId: string | null;
  onSaved: () => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export interface TaskEditorFormRef {
  save: () => void;
}

const STATUS_LABELS: Record<TaskStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  done: 'Done',
};

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  1: 'Highest',
  2: 'High',
  3: 'Medium',
  4: 'Low',
  5: 'Lowest',
};

const DEFAULT_TASK_VALUES = {
  title: '',
  status: 'open' as TaskStatus,
  priority: undefined as TaskPriority | undefined,
  dueAt: undefined as Date | undefined,
  tags: [] as string[],
  scope: 'personal' as TaskScope,
};

export const TaskEditorForm = forwardRef<TaskEditorFormRef, TaskEditorFormProps>(
  function TaskEditorForm({ mode, taskId, onSaved, onCancel, onDirtyChange }, ref) {
    const { t } = useTranslation(['notepad', 'common']);
    const updateTask = useUpdateTask();
    const createTask = useCreateTask();
    const { markItem } = useOnboarding();
    const teamId = useActiveTeamId();
    const titleInputRef = useRef<HTMLInputElement>(null);

    // Track initial values for dirty detection
    const initialValuesRef = useRef<typeof DEFAULT_TASK_VALUES | null>(null);

    // Fetch task data if editing
    const { data: task, isLoading: isLoadingTask } = useTask(taskId || '');

    // Form state
    const [title, setTitle] = useState(DEFAULT_TASK_VALUES.title);
    const [status, setStatus] = useState<TaskStatus>(DEFAULT_TASK_VALUES.status);
    const [priority, setPriority] = useState<TaskPriority | undefined>(
      DEFAULT_TASK_VALUES.priority
    );
    const [dueAt, setDueAt] = useState<Date | undefined>(DEFAULT_TASK_VALUES.dueAt);
    const [tags, setTags] = useState<string[]>(DEFAULT_TASK_VALUES.tags);
    const [tagInput, setTagInput] = useState('');
    const [scope, setScope] = useState<TaskScope>(DEFAULT_TASK_VALUES.scope);

    // Initialize form when task loads (edit mode)
    useEffect(() => {
      if (mode === 'edit' && task) {
        const initialValues = {
          title: task.title,
          status: task.status,
          priority: task.priority,
          dueAt: task.dueAt ? task.dueAt.toDate() : undefined,
          tags: task.tags || [],
          scope: 'personal' as TaskScope, // Existing tasks are personal by default
        };
        setTitle(initialValues.title);
        setStatus(initialValues.status);
        setPriority(initialValues.priority);
        setDueAt(initialValues.dueAt);
        setTags(initialValues.tags);
        setScope(initialValues.scope);
        initialValuesRef.current = initialValues;
      } else if (mode === 'create') {
        // Reset to defaults for create mode
        setTitle(DEFAULT_TASK_VALUES.title);
        setStatus(DEFAULT_TASK_VALUES.status);
        setPriority(DEFAULT_TASK_VALUES.priority);
        setDueAt(DEFAULT_TASK_VALUES.dueAt);
        setTags(DEFAULT_TASK_VALUES.tags);
        setScope(DEFAULT_TASK_VALUES.scope);
        initialValuesRef.current = DEFAULT_TASK_VALUES;
      }
    }, [mode, task]);

    // Track dirty state
    useEffect(() => {
      if (!initialValuesRef.current) return;

      const initial = initialValuesRef.current;
      const isDirty =
        title !== initial.title ||
        status !== initial.status ||
        priority !== initial.priority ||
        dueAt?.getTime() !== initial.dueAt?.getTime() ||
        JSON.stringify(tags) !== JSON.stringify(initial.tags);

      onDirtyChange?.(isDirty);
    }, [title, status, priority, dueAt, tags, onDirtyChange]);

    // Auto-focus title input when form mounts
    useEffect(() => {
      const timer = setTimeout(() => {
        titleInputRef.current?.focus();
        if (mode === 'edit') {
          titleInputRef.current?.select();
        }
      }, 100);
      return () => clearTimeout(timer);
    }, [mode, task]);

    const handleSave = useCallback(() => {
      // Validate
      if (!title.trim()) {
        return;
      }

      const input = {
        title: title.trim(),
        status,
        priority,
        dueAt: dueAt || null,
        tags: tags.length > 0 ? tags : undefined,
        scope,
        teamId: scope === 'team' ? teamId : undefined,
      };

      if (mode === 'edit' && taskId) {
        // Update existing task
        updateTask.mutate({ taskId, input }, { onSuccess: onSaved });
      } else {
        // Create new task
        createTask.mutate(
          { ...input, createdVia: 'task_editor' as const },
          {
            onSuccess: () => {
              // Mark onboarding checklist item
              markItem('task');
              onSaved();
            },
          }
        );
      }
    }, [
      title,
      dueAt,
      status,
      priority,
      tags,
      scope,
      teamId,
      mode,
      taskId,
      updateTask,
      createTask,
      markItem,
      onSaved,
    ]);

    // Expose save method via ref (must be after handleSave is defined)
    useImperativeHandle(
      ref,
      () => ({
        save: handleSave,
      }),
      [handleSave]
    );

    const handleAddTag = () => {
      const tag = tagInput.trim();
      if (tag && !tags.includes(tag)) {
        setTags([...tags, tag]);
        setTagInput('');
      }
    };

    const handleRemoveTag = (tag: string) => {
      setTags(tags.filter((t) => t !== tag));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSave();
      }
    };

    const isSaving = updateTask.isPending || createTask.isPending;

    // Show loading state while fetching task in edit mode
    if (mode === 'edit' && isLoadingTask) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    // If editing but task not found
    if (mode === 'edit' && !task && !isLoadingTask) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <p className="text-muted-foreground">{t('notepad:task.notFound', 'Task not found')}</p>
          <Button variant="outline" onClick={onCancel} className="mt-4">
            {t('common:actions.back', 'Back')}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full" onKeyDown={handleKeyDown}>
        {/* Form Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">{t('notepad:task.title', 'Title')}</Label>
            <Input
              ref={titleInputRef}
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('notepad:task.titlePlaceholder', 'Task title')}
              className="text-base"
            />
          </div>

          {/* Status and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">{t('notepad:task.status', 'Status')}</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as TaskStatus)}>
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">{t('notepad:task.priority', 'Priority')}</Label>
              <Select
                value={priority?.toString() || 'none'}
                onValueChange={(v) =>
                  setPriority(v === 'none' ? undefined : (Number(v) as TaskPriority))
                }
              >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('common:filters.all', 'None')}</SelectItem>
                  {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      P{value} - {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Scope (Team vs Personal) - only show in create mode */}
          {mode === 'create' && (
            <div className="space-y-2">
              <Label htmlFor="scope">Zichtbaarheid</Label>
              <Select value={scope} onValueChange={(v) => setScope(v as TaskScope)}>
                <SelectTrigger id="scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Persoonlijke taak</SelectItem>
                  <SelectItem value="team">Team taak (zichtbaar voor team)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Due Date and Time */}
          <div className="space-y-2">
            <Label>{t('notepad:task.dueDate', 'Deadline')}</Label>
            <DateTimePicker
              date={dueAt}
              onDateChange={setDueAt}
              placeholder="Kies datum en tijd"
              showTime={true}
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags">{t('notepad:note.tags', 'Tags')}</Label>
            <div className="flex gap-2">
              <Input
                id="tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddTag();
                  }
                }}
                placeholder={t('notepad:note.tagPlaceholder', 'Add tag (e.g., vehicle:ABC123)')}
              />
              <Button type="button" onClick={handleAddTag} variant="outline">
                {t('common:actions.add', 'Add')}
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-destructive"
                      aria-label={`Remove tag ${tag}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Keyboard hint */}
          <div className="text-xs text-muted-foreground text-center pt-2">
            Press <kbd className="px-1 py-0.5 bg-muted rounded">⌘</kbd>
            <kbd className="px-1 py-0.5 bg-muted rounded ml-1">Enter</kbd> to save
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="sticky bottom-0 flex justify-end gap-3 px-6 py-4 border-t bg-background">
          <Button variant="outline" onClick={onCancel}>
            {t('common:actions.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!title.trim() || isSaving}>
            {isSaving ? t('common:actions.saving', 'Saving...') : t('common:actions.save', 'Save')}
          </Button>
        </div>
      </div>
    );
  }
);
